import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import LocationAutocomplete from '../LocationAutocomplete';
import PlaceAutocomplete from '../PlaceAutocomplete';
import WhosGoingModal from '../WhosGoingModal';
import ExpandableCategorySelector from '../ExpandableCategorySelector';
import { specialAttributes } from '../../constants/searchFilterOptions';
import StartingPointService from '../../services/startingPointService';
import useToast from '../../hooks/useToast';
import useMediaQuery from '../../hooks/useMediaQuery';
import Button from '../shared/Button';
import Icon from '../shared/Icon';
import Select from '../shared/Select';
import Toast from '../shared/Toast';

// Quick-select text search chip ids (non-translatable identifiers). Labels
// and the canned query text live in src/locales/en/search.json under
// compactSearchBar.textSearchTemplates.<id>.
const TEXT_SEARCH_TEMPLATE_IDS = ['family', 'romantic', 'hidden', 'rainy'];

// Advanced filter select key stems + non-translatable option values. Labels
// live in src/locales/en/search.json under
// compactSearchBar.advancedFilters.<key>.label / .options.<value>.
const advancedFilterSelects = [
  { key: 'priceRange', options: [{ value: 'any' }, { value: 'free' }, { value: 'low' }, { value: 'medium' }, { value: 'high' }] }
];

// Distance option key stems (values are the real filter values sent to
// search; the key stem avoids "0.5" colliding with i18next's dot-nesting in
// translation keys). Labels live in search.json under
// compactSearchBar.distanceOptions.<key>.
const distanceOptions = [
  { value: '0.5', key: 'walking' },
  { value: '3', key: 'biking' },
  { value: '15', key: 'miles15' },
  { value: '50', key: 'miles50' },
  { value: '1000', key: 'any' }
];

// Quick date button key stems (non-translatable identifiers). Labels live in
// search.json under compactSearchBar.quickDates.<key>.
const quickDateButtons = [
  { key: 'today' },
  { key: 'tomorrow' },
  { key: 'weekend' },
  { key: 'week' }
];

/**
 * CompactSearchBar — collapsed search bar for the dashboard home page.
 * Shows location + dates + search in a single row.
 * Chevron expands to reveal advanced filters (categories, who's going, etc.).
 *
 * Below `sm` the bar is deliberately shorter (~170px vs ~320px) so the Coming Up
 * section is visible on first load: the Where field and an icon-only Search share
 * row one, the quick-date chips take row two, and the exact date pickers move into
 * the "More options" panel. `sm` and up are unchanged.
 */
function CompactSearchBar({ search }) {
  const { t } = useTranslation('search');
  // Tailwind's `sm` breakpoint. Drives only the one change CSS can't express:
  // relocating the date pickers into the advanced panel (see `whenField`).
  const isCompactLayout = !useMediaQuery('(min-width: 640px)');
  const [locatingGPS, setLocatingGPS] = useState(false);
  const [distanceHighlight, setDistanceHighlight] = useState(false);
  const [featuresOpen, setFeaturesOpen] = useState(false);
  const distanceRef = useRef(null);
  const isMountedRef = useRef(true);
  const highlightTimerRef = useRef(null);
  const highlightClearTimerRef = useRef(null);
  const { toast, showToast, hideToast } = useToast();

  const toggleFeature = (value) => {
    const current = search.searchFilters.specialAttributes;
    const updated = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    search.setSearchFilters(prev => ({ ...prev, specialAttributes: updated }));
  };

  const clearFeatures = () => {
    search.setSearchFilters(prev => ({ ...prev, specialAttributes: [] }));
  };

  // Cleanup timers and mounted flag on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
      clearTimeout(highlightTimerRef.current);
      clearTimeout(highlightClearTimerRef.current);
    };
  }, []);

  const handleNearMe = async () => {
    if (locatingGPS) return;
    setLocatingGPS(true);
    try {
      const coords = await StartingPointService.getCurrentLocation();
      if (!isMountedRef.current) return;
      // Anchor the search on GPS. `startingPointType` is the internal marker
      // for current-location searches; `location` is display text only (the
      // localized label shown in the Where input).
      search.setSearchFilters(prev => ({
        ...prev,
        location: t('labels:startingPoints.currentLocation.name'),
        locationCoords: { lat: coords.lat, lng: coords.lng },
        maxDistance: 0.5, // Walking distance
        startingPointType: 'current-location',
        startingPointAddress: '',
        startingPointCoords: { lat: coords.lat, lng: coords.lng }
      }));
      // Auto-expand advanced filters so user sees the distance change
      if (!search.showAdvancedFilters) {
        search.setShowAdvancedFilters(true);
      }
      // Flash highlight on the distance selector after a short delay for the expand animation
      clearTimeout(highlightTimerRef.current);
      clearTimeout(highlightClearTimerRef.current);
      highlightTimerRef.current = setTimeout(() => {
        if (!isMountedRef.current) return;
        setDistanceHighlight(true);
        distanceRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        highlightClearTimerRef.current = setTimeout(() => {
          if (isMountedRef.current) setDistanceHighlight(false);
        }, 1500);
      }, 350);
    } catch (error) {
      if (!isMountedRef.current) return;
      const msg = error.code === 1
        ? t('compactSearchBar.locationErrors.permissionDenied')
        : error.code === 3
        ? t('compactSearchBar.locationErrors.timeout')
        : t('compactSearchBar.locationErrors.generic');
      showToast(msg, 'error');
    } finally {
      if (isMountedRef.current) setLocatingGPS(false);
    }
  };

  // Hoisted out of the JSX below (rather than an inline array literal in the
  // .map()) — an inline array constructed inside a JSXExpressionContainer
  // still trips the i18next/no-literal-string false positive on the
  // (non-user-visible) 'type'/icon-name literals, same class of issue as the
  // toLocaleDateString hoists documented in FeedDatePicker.js.
  const startingPointTypeOptions = [
    { type: 'city-center', icon: 'building' },
    ...(search.searchFilters.startingPointType === 'current-location'
      ? [{ type: 'current-location', icon: 'crosshairs' }]
      : []),
    ...(search.homeLocation ? [{ type: 'home', icon: 'home' }] : []),
    { type: 'custom', icon: 'mapPin' }
  ];

  // Exact date range. Rendered in the main row at `sm`+, and inside the advanced
  // panel below it — two different DOM parents, so it's built once here rather
  // than duplicated (a second copy would give `data-tour="date-picker"` two
  // matches and querySelector-based targeting would hit the hidden one).
  const whenField = (
    <div className="min-w-0 xl:flex-1" data-tour="date-picker">
      <label className="block text-xs font-semibold text-content-secondary uppercase tracking-wide mb-1">{t('compactSearchBar.dates.label')}</label>
      <div className="flex items-center gap-2">
        <input
          type="date"
          value={search.searchFilters.startDate}
          onChange={(e) => {
            const newStart = e.target.value;
            search.handleFilterChange('startDate', newStart);
            if (!search.searchFilters.endDate || newStart > search.searchFilters.endDate) {
              search.handleFilterChange('endDate', newStart);
            }
          }}
          min={new Date().toISOString().split('T')[0]}
          className="w-full px-2 py-2 border border-border rounded-lg text-sm bg-surface text-content-primary placeholder:text-content-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
        />
        <span className="text-content-secondary text-xs">{t('compactSearchBar.dates.separator')}</span>
        <input
          type="date"
          value={search.searchFilters.endDate}
          onChange={(e) => search.handleFilterChange('endDate', e.target.value)}
          min={search.searchFilters.startDate || new Date().toISOString().split('T')[0]}
          className="w-full px-2 py-2 border border-border rounded-lg text-sm bg-surface text-content-primary placeholder:text-content-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
        />
      </div>
    </div>
  );

  return (
    <>
      {/* On the Mist page since the 2026-10-01 refresh — the gradient band
          that used to hold this is gone; white fields, filter-pill dates and a
          solid blue Search carry it. */}
      <div>
        <div className="max-w-6xl mx-auto px-4 pt-4 pb-1 sm:pt-6">
          {/* Compact search row */}
          {/* <sm: 2-column grid — Where and the icon-only Search share row 1, quick dates span row 2 */}
          {/* <lg: 2-column grid (Where/When on row 1, QuickDates/Search on row 2) — keeps date pickers from overflowing into adjacent columns at intermediate widths */}
          {/* Placement (rather than reordering the DOM) keeps tab order matching visual order at every width */}
          <div className="grid grid-cols-[minmax(0,1fr)_auto] sm:grid-cols-2 xl:flex xl:flex-row xl:items-end gap-2 sm:gap-3">
            {/* Where */}
            <div className="xl:flex-1 min-w-0" data-tour="location-input">
              <label className="hidden sm:block text-xs font-semibold text-content-secondary uppercase tracking-wide mb-1">{t('compactSearchBar.location.label')}</label>
              <div className="relative">
                <LocationAutocomplete
                  value={search.searchFilters.location}
                  onChange={(value) => {
                    search.handleFilterChange('location', value);
                    if (!value) search.handleFilterChange('locationCoords', null);
                  }}
                  onPlaceSelect={(place) => {
                    if (place?.location) {
                      search.setSearchFilters(prev => ({
                        ...prev,
                        location: place.name,
                        locationCoords: place.location
                      }));
                    }
                  }}
                  placeholder={t('compactSearchBar.location.placeholder')}
                  className="w-full h-11 sm:h-auto px-2 py-2 border border-border rounded-lg text-sm bg-surface text-content-primary placeholder:text-content-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
                />
                <button
                  type="button"
                  onClick={handleNearMe}
                  disabled={locatingGPS}
                  title={t('compactSearchBar.location.nearMeButtonLabel')}
                  aria-label={t('compactSearchBar.location.nearMeButtonLabel')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 min-w-[44px] min-h-[44px] sm:min-w-0 sm:min-h-0 sm:p-1 flex items-center justify-center hover:bg-surface-secondary active:scale-[0.98] rounded-full transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
                >
                  {locatingGPS ? (
                    <span className="w-4 h-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
                  ) : (
                    <Icon name="crosshairs" className={`w-4 h-4 ${search.searchFilters.startingPointType === 'current-location' ? 'text-primary' : 'text-content-secondary'}`} />
                  )}
                </button>
              </div>
            </div>

            {/* When — moves into the advanced panel below `sm` */}
            {!isCompactLayout && whenField}

            {/* Quick date buttons */}
            <div className="col-span-2 sm:col-span-1 flex flex-wrap gap-1">
              {quickDateButtons.map(({ key }) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => search.setQuickDate(key)}
                  // Filter pills: you're choosing, not doing (style guide: Border Radius)
                  className={`flex-1 sm:flex-none min-h-[44px] lg:min-h-0 px-3 py-2.5 lg:py-2 rounded-full text-xs font-medium border active:scale-[0.98] transition-colors duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light ${
                    search.isQuickDateSelected(key)
                      ? 'bg-primary border-primary text-content-inverse'
                      : 'bg-surface border-border text-content-secondary hover:text-content-primary hover:bg-surface-secondary'
                  }`}
                >
                  {t(`compactSearchBar.quickDates.${key}`)}
                </button>
              ))}
            </div>

            {/* Search button — pulled up beside Where on row 1 below `sm` */}
            <div className="row-start-1 col-start-2 sm:row-start-auto sm:col-start-auto" data-tour="search-button">
              {/* Icon-only 44px square on phones (max-sm: variants win over
                  Button's own size classes), labelled button from sm up */}
              <Button
                size="lg"
                icon="search"
                onClick={search.handleSearch}
                aria-label={t('compactSearchBar.searchButton')}
                className="max-sm:h-11 max-sm:w-11 max-sm:px-0 sm:px-6"
              >
                <span className="hidden sm:inline">{t('compactSearchBar.searchButton')}</span>
              </Button>
            </div>
          </div>

          {/* Expand/collapse toggle */}
          <div className="flex justify-center mt-1.5 sm:mt-2">
            <button
              onClick={() => search.setShowAdvancedFilters(!search.showAdvancedFilters)}
              className="text-content-secondary hover:text-content-primary text-xs font-medium flex items-center gap-1 transition-colors duration-fast px-3 py-2 min-h-[44px] sm:min-h-0 sm:py-1 rounded-full hover:bg-surface-tertiary active:scale-[0.98] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
            >
              {search.showAdvancedFilters ? t('compactSearchBar.toggleAdvanced.less') : t('compactSearchBar.toggleAdvanced.more')}
              <Icon name={search.showAdvancedFilters ? 'chevronUp' : 'chevronDown'} className="w-3 h-3" />
            </button>
          </div>

          {/* Advanced filters (collapsible) */}
          {search.showAdvancedFilters && (
            <div className="mt-3 pt-3 border-t border-border">
              {/* Sits above the filter panel, not inside it (it's built once and
                  also renders in the main row at sm+) */}
              {isCompactLayout && <div className="mb-3">{whenField}</div>}

              <div className="bg-surface rounded-card border border-border p-4">
                {/* Category-based search */}
                {search.searchFilters.searchMode === 'categories' && (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="relative" data-tour="category-selector">
                      <ExpandableCategorySelector
                        selectedItems={search.searchFilters.selectedCategoryItems}
                        onChange={search.handleExpandableCategoryChange}
                        compact={true}
                      />
                    </div>

                    <div className="relative">
                      <button
                        type="button"
                        onClick={() => setFeaturesOpen(o => !o)}
                        className={`h-11 sm:h-9 pl-3 pr-8 text-sm border rounded-full bg-surface cursor-pointer transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light flex items-center ${
                          search.searchFilters.specialAttributes.length > 0
                            ? 'border-primary text-primary-dark font-medium'
                            : 'border-border text-content-secondary hover:border-primary-light'
                        }`}
                      >
                        {search.searchFilters.specialAttributes.length > 0
                          ? t('compactSearchBar.features.countLabel', { count: search.searchFilters.specialAttributes.length })
                          : t('compactSearchBar.features.label')}
                      </button>
                      <span className="absolute right-2.5 top-1/2 -translate-y-1/2 pointer-events-none text-content-muted">
                        <Icon name="chevronDown" className={`w-3 h-3 transition-transform duration-normal ${featuresOpen ? 'rotate-180' : ''}`} />
                      </span>

                      {featuresOpen && (
                        <>
                          <div
                            className="fixed inset-0 z-overlay"
                            onClick={() => setFeaturesOpen(false)}
                          />
                          <div className="absolute top-full right-0 sm:right-auto sm:left-0 mt-1 min-w-[260px] w-max max-w-[calc(100vw-2rem)] bg-surface border border-border rounded-lg shadow-elevation-3 z-modal max-h-80 overflow-y-auto">
                            {specialAttributes.map(attr => {
                              const checked = search.searchFilters.specialAttributes.includes(attr.value);
                              return (
                                <label
                                  key={attr.value}
                                  className="flex items-center px-3 py-1.5 hover:bg-background cursor-pointer border-b border-border last:border-b-0"
                                >
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    onChange={() => toggleFeature(attr.value)}
                                    className="mr-2 h-3.5 w-3.5 text-primary rounded-lg focus-visible:ring-primary"
                                  />
                                  <span className="text-sm text-content-primary">{attr.label}</span>
                                </label>
                              );
                            })}
                            <div className="sticky bottom-0 bg-surface px-3 py-2 border-t border-border flex justify-between items-center">
                              <button
                                type="button"
                                onClick={clearFeatures}
                                disabled={search.searchFilters.specialAttributes.length === 0}
                                className="text-xs text-content-secondary hover:text-content-primary disabled:opacity-40 disabled:cursor-not-allowed"
                              >
                                {t('compactSearchBar.features.clearButton')}
                              </button>
                              <Button size="sm" onClick={() => setFeaturesOpen(false)}>
                                {t('compactSearchBar.features.doneButton')}
                              </Button>
                            </div>
                          </div>
                        </>
                      )}
                    </div>

                    <WhosGoingPill
                      whoGoing={search.searchFilters.whoGoing}
                      display={search.getWhosGoingDisplay()}
                      onClick={() => search.setShowWhosGoingModal(true)}
                    />

                    <div className="hidden md:block h-6 w-px bg-border/50" />

                    <button
                      type="button"
                      onClick={() => search.setSearchFilters(prev => ({ ...prev, searchMode: 'text' }))}
                      className="inline-flex items-center gap-0.5 text-sm text-primary hover:text-primary-dark font-medium active:scale-[0.98] transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
                    >
                      {t('compactSearchBar.categoryMode.switchToTextButton')}
                      <Icon name="chevronRight" className="w-4 h-4 flex-shrink-0" />
                    </button>
                  </div>
                )}

                {/* Text-based search */}
                {search.searchFilters.searchMode === 'text' && (
                  <div className="space-y-3">
                    <div className="flex items-center gap-3">
                      <div className="flex-1 relative">
                        <input
                          type="text"
                          value={search.searchFilters.textQuery}
                          onChange={(e) => search.setSearchFilters(prev => ({ ...prev, textQuery: e.target.value }))}
                          placeholder={t('compactSearchBar.textMode.placeholder')}
                          className="w-full h-10 pl-4 pr-10 text-sm border border-border rounded-full bg-surface text-content-primary placeholder:text-content-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light transition-colors duration-fast"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-content-muted"><Icon name="paintBrush" className="w-4 h-4" /></span>
                      </div>

                      <WhosGoingPill
                        whoGoing={search.searchFilters.whoGoing}
                        display={search.getWhosGoingDisplay()}
                        onClick={() => search.setShowWhosGoingModal(true)}
                      />

                      <button
                        type="button"
                        onClick={() => search.setSearchFilters(prev => ({ ...prev, searchMode: 'categories' }))}
                        className="text-sm text-primary hover:text-primary-dark font-medium active:scale-[0.98] transition-all duration-fast whitespace-nowrap focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light"
                      >
                        {t('compactSearchBar.textMode.switchToCategoriesButton')}
                      </button>
                    </div>

                    <div className="flex flex-wrap gap-2">
                      {TEXT_SEARCH_TEMPLATE_IDS.map(id => {
                        const templateQuery = t(`compactSearchBar.textSearchTemplates.${id}.query`);
                        return (
                          <button
                            key={id}
                            type="button"
                            onClick={() => search.setSearchFilters(prev => ({ ...prev, textQuery: templateQuery }))}
                            className={`px-3 py-1 text-xs rounded-full border transition-all duration-fast active:scale-[0.98] ${
                              search.searchFilters.textQuery === templateQuery
                                ? 'border-primary bg-primary text-content-inverse'
                                : 'border-border/50 bg-surface/50 text-content-secondary hover:border-primary-light hover:bg-surface'
                            }`}
                          >
                            {t(`compactSearchBar.textSearchTemplates.${id}.label`)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Price / Distance / Starting Point */}
                <div className="mt-3 pt-3 border-t border-border/30">
                  <div className="grid grid-cols-2 gap-3">
                    {advancedFilterSelects.map(({ key, options }) => (
                      <div key={key}>
                        <label className="block text-xs font-medium text-content-muted uppercase mb-1">{t(`compactSearchBar.advancedFilters.${key}.label`)}</label>
                        <Select
                          value={search.searchFilters[key]}
                          onChange={(e) => search.handleFilterChange(key, e.target.value)}
                          size={null}
                          className="h-11 sm:h-9 px-2 text-sm sm:text-xs"
                        >
                          {options.map(opt => (
                            <option key={opt.value} value={opt.value}>{t(`compactSearchBar.advancedFilters.${key}.options.${opt.value}`)}</option>
                          ))}
                        </Select>
                      </div>
                    ))}

                    <div ref={distanceRef}>
                      <label className="block text-xs font-medium text-content-muted uppercase mb-1">{t('compactSearchBar.distanceLabel')}</label>
                      <Select
                        value={search.searchFilters.maxDistance}
                        onChange={(e) => search.setSearchFilters(prev => ({ ...prev, maxDistance: parseFloat(e.target.value) }))}
                        size={null}
                        className={`h-11 sm:h-9 px-2 text-sm sm:text-xs transition-all duration-slow ${
                          distanceHighlight ? 'border-primary ring-2 ring-primary/40 bg-primary-lighter' : ''
                        }`}
                      >
                        {distanceOptions.map(opt => (
                          <option key={opt.value} value={opt.value}>{t(`compactSearchBar.distanceOptions.${opt.key}`)}</option>
                        ))}
                      </Select>
                    </div>

                    <div className="col-span-2 flex flex-wrap items-center gap-2 pt-2 border-t border-border/30 mt-1">
                      <span className="text-xs font-medium text-content-muted uppercase">{t('compactSearchBar.startingPoint.fromLabel')}</span>
                      <div className="flex flex-wrap gap-1.5">
                        {startingPointTypeOptions.map(({ type, icon }) => (
                          <button
                            key={type}
                            type="button"
                            disabled={type === 'current-location'}
                            aria-label={type === 'current-location' ? t('compactSearchBar.startingPoint.currentLocationAriaLabel') : undefined}
                            onClick={() => {
                              if (type === 'current-location') return;
                              search.setSearchFilters(prev => ({
                                ...prev,
                                startingPointType: type,
                                startingPointAddress: '',
                                startingPointCoords: null
                              }));
                            }}
                            className={`px-2.5 py-1 text-xs rounded-full border transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light ${
                              type === 'current-location'
                                ? 'border-primary bg-primary-lighter text-primary-dark font-medium cursor-default'
                                : search.searchFilters.startingPointType === type
                                  ? 'border-primary bg-primary-lighter text-primary-dark font-medium active:scale-[0.98]'
                                  : 'border-border bg-surface text-content-secondary hover:border-primary-light active:scale-[0.98]'
                            }`}
                          >
                            <Icon name={icon} className="w-3 h-3 inline-block mr-0.5" />{t(`compactSearchBar.startingPoint.types.${type}`)}
                          </button>
                        ))}
                      </div>

                      {search.searchFilters.startingPointType === 'custom' && (
                        <div className="flex-1 min-w-[200px]">
                          <PlaceAutocomplete
                            value={search.searchFilters.startingPointAddress}
                            onChange={(value) => search.setSearchFilters(prev => ({ ...prev, startingPointAddress: value }))}
                            onPlaceSelect={(place) => {
                              search.setSearchFilters(prev => ({
                                ...prev,
                                startingPointAddress: place.address || place.name,
                                startingPointCoords: place.location
                              }));
                            }}
                            placeholder={t('compactSearchBar.startingPoint.addressPlaceholder')}
                            className="h-11 sm:h-9 text-sm sm:text-xs"
                          />
                        </div>
                      )}

                      {search.searchFilters.startingPointType === 'current-location' && (
                        <span className="text-xs text-primary flex items-center"><Icon name="crosshairs" className="w-3 h-3 mr-1" />{t('compactSearchBar.startingPoint.usingGpsLabel')}</span>
                      )}

                      {search.searchFilters.startingPointType === 'home' && search.homeLocation && (
                        <span className="text-xs text-primary flex items-center"><Icon name="mapPin" className="w-3 h-3 mr-1" />{search.homeLocation}</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Who's Going Modal */}
      <WhosGoingModal
        isOpen={search.showWhosGoingModal}
        onClose={() => search.setShowWhosGoingModal(false)}
        onSave={search.handleWhosGoingSave}
        initialSelectedPeople={search.searchFilters.whoGoing}
        initialWeights={search.searchFilters.groupWeights}
        showPriority
      />

      {toast && (
        <Toast message={toast.message} variant={toast.variant} onClose={hideToast} />
      )}
    </>
  );
}

function WhosGoingPill({ whoGoing, display, onClick }) {
  return (
    <button
      onClick={onClick}
      data-tour="whos-going"
      className={`h-11 sm:h-9 px-4 text-sm border rounded-full transition-all duration-fast focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-light flex items-center gap-2 whitespace-nowrap ${
        whoGoing.length > 0
          ? 'border-primary bg-primary-lighter text-primary-dark font-medium'
          : 'border-border bg-surface text-content-secondary hover:border-primary-light'
      }`}
    >
      <Icon name="userGroup" className="w-4 h-4" />
      <span>{display}</span>
      {whoGoing.length > 0 && (
        <div className="flex -space-x-1 ml-1">
          {whoGoing.slice(0, 2).map((person, index) => (
            <span key={`${person.id}-${person.type || 'user'}-${index}`} className="text-xs">
              {person.avatar}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

export default CompactSearchBar;
